<script setup lang="ts">
	import { useWindowSize } from '@vueuse/core'
	import type { IconContext } from '~/components/Shared/benefits-brief.vue'

	interface NavItemsProps {
		label: string
		href: string
		icon?: {
			active: IconContext
			inactive: IconContext
		}
		disabled?: boolean
		id?: string
		tourGuide?: {
			title: string
			description: string
			cover?: unknown
			target: Function
		}
	}

	interface NavProps {
		category?: string
		navItems: NavItemsProps[]
	}

	const emit = defineEmits<{
		(arg: 'update:show', param: boolean): void
		(arg: 'closeSideBar'): void
	}>()

	const props = withDefaults(defineProps<{ show?: boolean; discount?: number }>(), {
		show: true,
		discount: 5
	})
	const { isMobile } = useMobile()
	const { width } = useWindowSize()
	const { disableTour, User } = useUser()
	const mixpanel = useMixpanel()
	const route = useRoute()
	const isReferralOpen = shallowRef(false)
	const current = ref(0)
	const isTourOpen = shallowRef(false)
	const { hasAnyPermission } = reactor(usePermissionStore())

	const currentPath = computed(() => {
		const { path } = useRoute()
		return path.split('/')[1]
	})

	const navigationList = computed<NavProps[]>(() => [
		{
			navItems: [
				{
					label: 'Dashboard',
					href: '/',
					id: 'dashboard',
					icon: {
						inactive: {
							name: 'dashboard',
							dir: 'line'
						},
						active: {
							name: 'dashboard-active',
							dir: 'fill'
						}
					},
					tourGuide: {
						description:
							'The Dashboard is where you get to see all the activities happening on your business account by your staff members. You get to also see the updates and key things to do if you have any',
						title: 'Welcome, take a tour guide to get started',
						target: () => document.querySelector('[data-id="dashboard"]')
					}
				}
			]
		},
		{
			category: 'ADMIN',
			navItems: [
				{
					label: 'My Plans',
					id: 'cover',
					href: '/mycover',
					icon: {
						inactive: {
							name: 'mycover',
							dir: 'line'
						},
						active: {
							name: 'mycover-active',
							dir: 'fill'
						}
					},
					disabled: !hasAnyPermission.value([PERMISSIONS.PLAN.VIEW_DETAILS, PERMISSIONS.PLAN.PURCHASE]),
					tourGuide: {
						description:
							'This is where you manage your plans and activities each of your staff members performed',
						title: 'My plans',
						target: () => document.querySelector('[data-id="cover"]')
					}
				},
				{
					label: 'Claims',
					href: '/claims',
					icon: {
						inactive: {
							name: 'claims',
							dir: 'line'
						},
						active: {
							name: 'clipboard-document',
							dir: 'documents'
						}
					},
					id: 'claims',
					disabled: !hasAnyPermission.value([
						PERMISSIONS.CLAIM.VIEW_DETAILS,
						PERMISSIONS.CLAIM.VIEW_LIST,
						PERMISSIONS.CLAIM.MAKE
					]),
					tourGuide: {
						description:
							'This is where you get to  manage, make and accept offers of claims made on this business account.',
						title: 'Claims',
						target: () => document.querySelector('[data-id="claims"]')
					}
				},
				{
					label: 'Products',
					href: '/products',
					icon: {
						active: {
							name: 'products-active',
							dir: 'fill'
						},
						inactive: {
							name: 'products',
							dir: 'line'
						}
					},
					disabled: !hasAnyPermission.value([PERMISSIONS.PRODUCT.VIEW]),
					id: 'products',
					tourGuide: {
						description:
							'This is where you get to buy different insurance product based on their categories.',
						title: 'Products',
						target: () => document.querySelector('[data-id="products"]')
					}
				},
				{
					label: 'Staff',
					href: '/employees',
					icon: {
						active: {
							name: 'employee',
							dir: 'fill',
							pathClass: 'fill-blue-950'
						},
						inactive: {
							name: 'employee',
							dir: 'line'
						}
					},
					id: 'employees',
					tourGuide: {
						description:
							'When a staff activates their policy, then they are considered as an employee. Only then can they be viewed from the this page.',
						title: 'Staff',
						target: () => document.querySelector('[data-id="employees"]')
					}
				}
			]
		},
		{
			category: 'TRANSACTION',
			navItems: [
				{
					label: 'Wallet',
					href: '/wallet',
					icon: {
						active: {
							name: 'wallet-active',
							dir: 'fill'
						},
						inactive: {
							name: 'wallet',
							dir: 'line'
						}
					},
					id: 'wallet',
					tourGuide: {
						description:
							'When a staff activates their policy, then they are considered as an employee. Only then can they be viewed from the this page.',
						title: 'Wallet',
						target: () => document.querySelector('[data-id="wallet"]')
					},
					disabled: !hasAnyPermission.value([
						PERMISSIONS.WALLET.ADD_CARD,
						PERMISSIONS.WALLET.VIEW_BALANCE,
						PERMISSIONS.WALLET.FUND,
						PERMISSIONS.WALLET.WITHDRAW
					])
				},
				{
					label: 'Transaction history',
					href: '/transaction-history',
					icon: {
						active: {
							name: 'clock-active',
							dir: 'fill'
						},
						inactive: {
							name: 'clock',
							dir: 'line'
						}
					},
					disabled: !hasAnyPermission.value([PERMISSIONS.TRANSACTION.VIEW_DETAILS]),
					id: 'transaction-history',
					tourGuide: {
						description:
							'When a staff activates their policy, then they are considered as an employee. Only then can they be viewed from the this page.',
						title: 'Transaction history',
						target: () => document.querySelector('[data-id="transaction-history"]')
					}
				},
				{
					label: 'Invoice',
					href: '/invoice',
					icon: {
						active: {
							name: 'invoice-active',
							dir: 'fill'
						},
						inactive: {
							name: 'invoice',
							dir: 'line'
						}
					},
					id: 'invoice',
					tourGuide: {
						description:
							'When a staff activates their policy, then they are considered as an employee. Only then can they be viewed from the this page.',
						title: 'Invoice',
						target: () => document.querySelector('[data-id="invoice"]')
					}
				},
				{
					label: 'Rewards',
					href: '/rewards',
					icon: {
						active: {
							name: 'medal-active',
							dir: 'fill'
						},
						inactive: {
							name: 'medal',
							dir: 'line'
						}
					},
					id: 'rewards',
					tourGuide: {
						description:
							'When a staff activates their policy, then they are considered as an employee. Only then can they be viewed from the this page.',
						title: 'Rewards',
						target: () => document.querySelector('[data-id="rewards"]')
					}
				}
			]
		},
		{
			navItems: [
				{
					label: 'Settings',
					href: '/settings',
					icon: {
						active: {
							name: 'setting-active',
							dir: 'fill'
						},
						inactive: {
							name: 'setting',
							dir: 'line'
						}
					},
					id: 'settings',
					tourGuide: {
						description:
							'When a staff activates their policy, then they are considered as an employee. Only then can they be viewed from the this page.',
						title: 'settings',
						target: () => document.querySelector('[data-id="settings"]')
					},
					disabled: !hasAnyPermission.value([
						PERMISSIONS.ROLE.VIEW,
						PERMISSIONS.ROLE.CREATE,
						PERMISSIONS.ROLE.EDIT,
						PERMISSIONS.ROLE.VIEW_LIST,
						PERMISSIONS.TRANSACTION.VIEW_DETAILS,
						PERMISSIONS.TEAM.VIEW_MEMBERS
					])
				}
			]
		}
	])
	const tourGuide = computed(() => {
		// Make a list of tour guides from the nav items in the list.
		const tourRecord: {
			title: string
			description: string
			cover?: VNode
			target: HTMLElement | (() => HTMLElement) | (() => null) | undefined
		}[] = []

		for (let i = 0; i < navigationList.value.length; i++) {
			const navList = navigationList.value[i]?.navItems || []
			// Push all the tour in each nav list to tour record
			for (let index = 0; index < navList.length; index++) {
				const listItem = navList[index]
				if (listItem && !listItem.disabled && listItem.tourGuide) {
					tourRecord.push({
						title: listItem.tourGuide.title,
						description: listItem.tourGuide.description || '',
						cover: listItem.tourGuide.cover as VNode | undefined,
						target: listItem.tourGuide.target as HTMLElement | (() => HTMLElement) | undefined
					})
				}
			}
		}
		return tourRecord
	})
	const toggle = computed(() => {
		return props.show ? 'w-[280px] max-w-[280px] z-[100]' : 'w-[0] opacity-0 -z-10'
	})

	const isActive = (href: string) => {
		return href === '/' ? route.matched[0]?.name === 'index' : route.fullPath.includes(href)
	}

	const closeSidebarOnMobile: () => void = () => {
		if (isMobile.value) {
			emit('update:show', false)
		}
	}
	const logout = () => {
		// This clears data attributed to a user when that user logs out
		resolve(() => mixpanel?.track('account_logout_biz'))
		mixpanel?.reset()
		useAuth().logout()
	}

	watch(width, e => {
		if (e <= 1023) {
			emit('update:show', false)
		} else if (e > 1023 && !props.show) {
			emit('update:show', true)
		}
	})

	const refer = () => {
		resolve(() =>
			mixpanel?.track('share-referral-link', {
				business_name: User?.customer?.name ?? '',
				email: User?.email ?? ''
			})
		)
		isReferralOpen.value = true
	}
</script>

<template>
	<div
		v-show="show"
		class="overlay visible absolute left-0 top-0 z-[110] h-full w-full bg-gray-500 bg-gradient-to-br from-0% to-15% mix-blend-multiply lg:invisible"
		@click.prevent="emit('update:show', false)"
	/>
	<aside
		ref="sidebar"
		class="invisible-scrollbar sidebar fixed top-0 z-[120] h-screen max-w-[300px] overflow-y-auto bg-primary-default transition-all duration-300 lg:relative"
		:class="[toggle]"
	>
		<div class="sticky top-0 bg-primary-default pb-8 pt-5">
			<lazy-tools-icon
				dir="other"
				icon="brand"
				svg-class="mx-4"
				aria-label="Mycovergenius"
				role="link"
				aria-roledescription="This is a company logo that you use to get to home page"
			/>
		</div>

		<nav>
			<ul class="mt- flex-1 px-4">
				<li v-for="{ category, navItems } in navigationList" :key="category" class="mb-6">
					<p v-if="category" class="mb-4 ml-4 text-sm font-medium text-white">
						{{ category }}
					</p>
					<template v-for="{ disabled, label, href, icon, ...rest } in navItems" :key="label">
						<div class="flex flex-col" @click="closeSidebarOnMobile">
							<nuxt-link
								v-if="!disabled"
								:to="href"
								class="flex min-h-[42px] w-full items-center gap-2 rounded px-4 text-sm text-primary-100"
								:class="{ 'router-link-active': currentPath === href.replace('/', '') }"
								:data-id="rest?.id"
							>
								<lazy-tools-icon
									:icon="(isActive(href) ? icon?.active.name : icon?.inactive.name) || ''"
									:dir="(isActive(href) ? icon?.active.dir : icon?.inactive.dir) || ''"
									:height="isActive(href) ? icon?.active?.height : icon?.inactive?.height"
									:width="isActive(href) ? icon?.active?.width : icon?.inactive?.width"
									:path-class="isActive(href) ? icon?.active?.pathClass : icon?.inactive?.pathClass"
									class="mr-1"
								/>
								<span class="label transition-all duration-200 hover:font-medium">
									{{ label }}
								</span>
							</nuxt-link>
						</div>
					</template>
				</li>
				<li v-show="isMobile" v class="mt-3">
					<div class="flex w-full">
						<button
							class="flex min-h-[42px] w-full items-center gap-2 rounded px-4 text-sm text-primary-100"
							@click="logout"
						>
							<lazy-tools-icon
								:height="20"
								:width="20"
								path-fill="#BCE1DA"
								icon="logout"
								dir="line"
								class="mr-1"
							/>
							<span class="label transition-all duration-200 hover:font-medium"> logout </span>
						</button>
					</div>
				</li>
			</ul>
		</nav>

		<div
			class="relative mx-2 mb-10 mt-5 flex min-h-[206px] w-[248px] flex-col items-center rounded-xl bg-success-50 py-5 pt-8"
		>
			<lazy-tools-icon icon="refer-tag" dir="other" svg-class="absolute top-2 left-0 h-[56px] w-[56px]" />

			<p class="mb-4 text-sm font-[800] text-gray-800">Refer and earn!</p>

			<p class="mb-3 max-w-[204px] text-center text-sm font-medium text-gray-700">
				Invite a friend and earn when they purchase their first plan.
			</p>

			<button
				type="button"
				class="mt- h-[36px] w-4/6 flex-none rounded-lg bg-white text-base font-[700] capitalize text-primary-2 hover:bg-primary-500 hover:text-white"
				@click="refer"
			>
				Share Invite
			</button>
		</div>
	</aside>
	<dialogs-referral v-model:show="isReferralOpen" />
	<a-tour
		v-model:current="current"
		:open="isTourOpen"
		:steps="tourGuide"
		placement="left"
		@close="disableTour(() => (isTourOpen = false))"
	/>
</template>

<style scoped>
	.active-svg {
		@apply invisible;
	}

	.label {
		@apply text-primary-250;
	}

	.router-link-active {
		@apply bg-primary-50;
	}

	.router-link-active .label {
		@apply text-blue-950;
	}
</style>
